/**
 * Hộp "Nhập mã của bạn" (v0.23.1, ADR-123; B-N1, B-L3): việc chạm tới một CHỦ TIỆM cần mã đăng nhập CỦA NGƯỜI ĐANG LÀM. Một hành động
 * chính: "Xác nhận" (khoá khi ô trống); "Thôi" là nút phụ. Câu hiện là CÂU CỦA MÁY CHỦ (đúng việc đang làm). Dùng ở Đặt mã / Đăng xuất /
 * Cho nghỉ / Đi làm lại qua `useHoiMaCuaToi`.
 *
 * Vòng sửa 1 (G-N1): bấm "Xác nhận" hộp KHÔNG đóng — nút khoá và đổi chữ "Đang kiểm…" trong lúc máy chủ kiểm mã; chỉ đóng khi việc làm
 * xong (hoặc bấm "Thôi" trước đó). Mã sai / lỗi: hộp vẫn nguyên, ô được xoá và câu lỗi hiện ngay dưới ô. Enter = Xác nhận (giữ phím
 * không gửi thêm); Esc / nền / "Thôi" = Thôi — bị bỏ qua lúc đang kiểm vì mã đã gửi đi rồi.
 */
import { KeyRound } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
  cauLoiTrongHop,
  chayCoMa,
  type KetQuaMa,
  type NoiDungHoiMa,
  phimXacNhan,
} from './hoi-ma-cua-toi.ts'
import { T } from './text/vi.ts'
import { To } from './ui.tsx'

export function HoiMaCuaToi({
  cau,
  loi,
  lan,
  dangKiem,
  onXacNhan,
  onThoi,
}: {
  cau: string
  loi: string | null
  /** Tăng mỗi lần máy chủ hỏi lại (mã sai): ô mã được xoá trống, hộp KHÔNG dựng lại. */
  lan: number
  dangKiem: boolean
  onXacNhan: (ma: string) => void
  onThoi: () => void
}) {
  const [ma, setMa] = useState('')
  const [lanDaThay, setLanDaThay] = useState(lan)
  if (lanDaThay !== lan) {
    setLanDaThay(lan)
    setMa('')
  }
  const o = useRef<HTMLInputElement>(null)
  // Gõ mã ngay khi hộp mở, và quay lại ô sau mỗi lần kiểm (bấm nút trên máy tính làm mất tiêu điểm).
  useEffect(() => {
    if (!dangKiem) o.current?.focus()
  }, [dangKiem])
  const thoi = dangKiem ? () => undefined : onThoi
  return (
    // Không bọc <form>: `.to` là cột flex (đầu · thân cuộn · chân), một <form> chen giữa phá bố cục. Enter trong ô = "Xác nhận".
    <To nhan={T.pinConfirmTitle} onDong={thoi}>
      <header className="to-dau">
        <h2>{T.pinConfirmTitle}</h2>
      </header>
      <div className="to-than">
        <div className="truong">
          <p className="chu-phu">{cau}</p>
          <div className="o-nhap-icon">
            <KeyRound size={20} />
            <input
              ref={o}
              id="hoi-ma-cua-toi"
              className="o-nhap"
              type="password"
              value={ma}
              placeholder={T.pinPlaceholder}
              aria-label={T.staffPhonePinLabel}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-invalid={loi !== null}
              readOnly={dangKiem}
              onChange={(e) => setMa(e.target.value)}
              onKeyDown={(e) => {
                if (phimXacNhan(e, ma, dangKiem)) onXacNhan(ma)
              }}
            />
          </div>
          {loi !== null && !dangKiem && (
            <p className="chu-loi" role="alert">
              {loi}
            </p>
          )}
        </div>
      </div>
      <footer className="to-chan nghi-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          disabled={dangKiem || ma.trim() === ''}
          onClick={() => onXacNhan(ma)}
        >
          {dangKiem ? T.pinChecking : T.pinConfirmOk}
        </button>
        <button type="button" className="nut nut-vien nut-lon" disabled={dangKiem} onClick={thoi}>
          {T.pinConfirmCancel}
        </button>
      </footer>
    </To>
  )
}

interface DangHoi extends NoiDungHoiMa {
  lan: number
  dangKiem: boolean
}

/**
 * `chay(goi)` = `chayCoMa` + hộp: trả `{ket:'xong', gia}` khi đã làm xong (lần đầu không cần mã, hoặc sau khi gõ đúng mã),
 * `{ket:'thoi'}` khi bấm "Thôi". `hop` là phần tử phải được vẽ ở nơi dùng. Lỗi khác `canMa` trước khi hộp mở thì `chay` ném như thường;
 * sau khi đã gõ mã thì lỗi hiện dưới ô (hộp không đóng). Hộp chỉ đóng khi `chay` kết thúc.
 */
export function useHoiMaCuaToi(): {
  chay: <R>(goi: (ma: string | undefined) => Promise<R>) => Promise<KetQuaMa<R>>
  hop: ReactNode
} {
  const [dangHoi, setDangHoi] = useState<DangHoi | null>(null)
  const tra = useRef<((ma: string | null) => void) | null>(null)
  const lan = useRef(0)
  const con = useRef(true)
  useEffect(() => {
    con.current = true
    return () => {
      con.current = false
      // Rời màn khi hộp đang mở = "Thôi" (không làm gì thêm).
      tra.current?.(null)
    }
  }, [])

  const hoiMa = useCallback(
    (x: NoiDungHoiMa) =>
      new Promise<string | null>((xong) => {
        tra.current = (ma) => {
          tra.current = null
          // Gõ mã: hộp GIỮ mở, chuyển sang "đang kiểm". "Thôi": `chay` đóng hộp ở `finally`.
          if (ma !== null && con.current)
            setDangHoi((d) => (d === null ? d : { ...d, dangKiem: true }))
          xong(ma)
        }
        lan.current += 1
        setDangHoi({ ...x, lan: lan.current, dangKiem: false })
      }),
    [],
  )
  const chay = useCallback(
    async <R,>(goi: (ma: string | undefined) => Promise<R>) => {
      try {
        return await chayCoMa(goi, hoiMa, cauLoiTrongHop)
      } finally {
        if (con.current) setDangHoi(null)
      }
    },
    [hoiMa],
  )

  return {
    chay,
    hop:
      dangHoi === null ? null : (
        <HoiMaCuaToi
          cau={dangHoi.cau}
          loi={dangHoi.loi}
          lan={dangHoi.lan}
          dangKiem={dangHoi.dangKiem}
          onXacNhan={(ma) => tra.current?.(ma)}
          onThoi={() => tra.current?.(null)}
        />
      ),
  }
}
